import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAccess } from '../components/access/AccessProvider';
import { User, Monitor, Zap, EuroIcon, HelpCircle } from 'lucide-react';
import { Container } from '../components/Container';
import { TabNavigation } from '../components/account/TabNavigation';
import { ProfileTab } from '../components/account/ProfileTab';
import { PreferencesTab } from '../components/account/PreferencesTab';

import { CreditsTab } from '../components/account/CreditsTab';
import { PricingTab } from '../components/account/PricingTab';
import { HelpTab } from '../components/account/HelpTab';
// import { CreditCalculatorTab } from '../components/account/CreditCalculatorTab';

export default function Account() {
  const { user } = useAuth();
  const { access } = useAccess();

  // Build validTabs as an array of tab objects - developer platform only needs basic tabs
  const validTabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'preferences', label: 'Preferences', icon: Monitor },
    ...(access?.features.has('billing_credits') &&
    !access?.permissions.has('billing:ignore_credits')
      ? [
          { id: 'credits', label: 'API Credits', icon: Zap },
          { id: 'pricing', label: 'Token Pricing', icon: EuroIcon },
        ]
      : []),
    { id: 'help', label: 'Need Help?', icon: HelpCircle },
  ];

  // Use validTabs for initial tab selection
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const validTabIds = validTabs.map((tab) => tab.id);
    return tabParam && validTabIds.includes(tabParam) ? tabParam : 'profile';
  });

  // Store payment status from URL before clearing
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);

  // Clear URL parameters after reading them
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');

    // Store payment status if present
    if (payment) {
      setPaymentStatus(payment);
    }

    // Clear both tab and payment parameters
    let shouldUpdateUrl = false;
    if (urlParams.has('tab')) {
      urlParams.delete('tab');
      shouldUpdateUrl = true;
    }
    if (urlParams.has('payment')) {
      urlParams.delete('payment');
      shouldUpdateUrl = true;
    }

    if (shouldUpdateUrl) {
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Clear payment status when switching tabs
  useEffect(() => {
    if (paymentStatus) {
      setPaymentStatus(null);
    }
  }, [activeTab, paymentStatus]);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">Please log in to view your account.</p>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab user={user} />;
      case 'preferences':
        return <PreferencesTab />;

      case 'credits':
        return <CreditsTab paymentStatus={paymentStatus} />;
      case 'pricing':
        return <PricingTab />;
      case 'help':
        return <HelpTab />;
      default:
        return <ProfileTab user={user} />;
    }
  };

  return (
    <Container className="max-w-4xl">
      {/* Page Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
          Account Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Mobile: Single column with horizontal tabs */}
      <div className="lg:hidden space-y-4">
        <TabNavigation tabs={validTabs} activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Desktop: Grid layout with vertical sidebar */}
      <div className="hidden lg:grid lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <TabNavigation tabs={validTabs} activeTab={activeTab} onTabChange={setActiveTab} />
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </Container>
  );
}
